import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Lock, Sparkles, Calendar, ArrowRight, PlayCircle, X } from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";

const PHASE_COLORS = {
  Detox: "var(--accent)",
  Restore: "var(--text-primary)",
  Burn: "var(--accent)",
  Reset: "var(--text-primary)",
};

export default function ClientMTM() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replays, setReplays] = useState([]);
  const [watching, setWatching] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/mtm/me").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/mtm/replays").then((r) => setReplays(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="lg:mt-0 mt-10 overline">Loading the method…</div>;

  if (!data?.enrolled) {
    return (
      <div className="lg:mt-0 mt-10 max-w-2xl" data-testid="mtm-not-enrolled">
        <p className="overline">Metabolic Transformation Method</p>
        <h1 className="font-display text-4xl mt-6 leading-tight">You&apos;re not enrolled<br />
          <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>in MTM yet.</span>
        </h1>
        <div className="bronze-rule mt-6" />
        <p className="font-soft text-lg mt-6" style={{ color: "var(--text-secondary)" }}>
          Once your coach enrolls you in a cohort, your phase-by-phase content will appear here.
        </p>
      </div>
    );
  }

  const { weeks, current_week, cohort } = data;
  // Group by phase
  const phases = ["Detox", "Restore", "Burn", "Reset"];
  const byPhase = phases.map((p) => ({ name: p, weeks: weeks.filter((w) => w.phase === p) }));
  const currentWeek = weeks.find((w) => w.is_current);
  const nextWeek = weeks.find((w) => w.is_preview);

  return (
    <div className="space-y-12 lg:mt-0 mt-10" data-testid="client-mtm">
      <header>
        <p className="overline">Metabolic Transformation Method</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
          You are in <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>week {current_week}.</span>
        </h1>
        <div className="bronze-rule mt-6" />
        <p className="font-soft text-lg mt-6" style={{ color: "var(--text-secondary)" }}>
          {cohort.name} · Phase: {currentWeek?.phase}
        </p>
      </header>

      {/* This week tile */}
      {currentWeek && (
        <Link to={`/app/program/week/${currentWeek.week}`} data-testid="this-week-tile"
          className="block p-10 md:p-12 transition-colors hover:bg-surface-2"
          style={{ background: "var(--ink)", color: "#FFFFFF" }}>
          <div className="flex items-start gap-6 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <p className="overline" style={{ color: "var(--accent)" }}>This week · 0{currentWeek.phase === 'Detox' ? '1' : currentWeek.phase === 'Restore' ? '2' : currentWeek.phase === 'Burn' ? '3' : '4'} · {currentWeek.phase}</p>
              <h2 className="font-display text-3xl sm:text-4xl mt-4 leading-tight" style={{ color: "#FFFFFF" }}>
                Week {currentWeek.week} — {currentWeek.title}
              </h2>
              <div className="bronze-rule mt-5" />
              <p className="font-soft text-lg mt-6 max-w-2xl" style={{ color: "rgba(255,255,255,0.8)" }}>
                {currentWeek.summary || "Open this week to begin."}
              </p>
            </div>
            <div className="self-center font-label text-[10px] tracking-[0.28em] uppercase flex items-center gap-3" style={{ color: "var(--accent)" }}>
              Open week <ArrowRight size={14} />
            </div>
          </div>
        </Link>
      )}

      {/* Next week preview */}
      {nextWeek && (
        <div className="p-8" style={{ background: "var(--surface-2)", border: "0.5px solid var(--border-strong)" }} data-testid="next-week-preview">
          <div className="flex items-start gap-4">
            <Sparkles size={16} style={{ color: "var(--accent)" }} className="mt-1" />
            <div>
              <p className="overline">Coming next week</p>
              <h3 className="font-display text-2xl mt-3">Week {nextWeek.week} — {nextWeek.title}</h3>
              <p className="font-soft text-base mt-3" style={{ color: "var(--text-secondary)" }}>
                {nextWeek.summary || "Stay tuned."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Call replays */}
      {replays.length > 0 && (
        <section data-testid="replay-library">
          <p className="overline">Group call replays</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {replays.map((r) => (
              <button key={r.replay_id} onClick={() => setWatching(r)}
                className="card-luxe text-left block hover:border-luxe-strong"
                data-testid={`client-replay-${r.replay_id}`}>
                <p className="overline flex items-center gap-2">
                  <PlayCircle size={12} style={{ color: "var(--accent)" }} /> {r.date}{r.week_number ? ` · Week ${r.week_number}` : ""}
                </p>
                <h4 className="font-display text-lg mt-3">{r.title}</h4>
                {r.description && <p className="font-soft text-sm mt-2 line-clamp-2" style={{ color: "var(--text-secondary)" }}>{r.description}</p>}
                <span className="font-label text-[10px] tracking-[0.24em] uppercase mt-3 inline-flex items-center gap-2" style={{ color: "var(--accent)" }}>
                  Watch replay <ArrowRight size={11} />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {watching && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6" style={{ background: "rgba(10,9,8,0.8)" }}
          onClick={() => setWatching(null)} data-testid="replay-modal">
          <div className="card-luxe max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="overline">{watching.date}{watching.week_number ? ` · Week ${watching.week_number}` : ""}</p>
                <h2 className="font-display text-2xl mt-2">{watching.title}</h2>
              </div>
              <button className="btn-ghost" onClick={() => setWatching(null)} data-testid="close-replay-modal"><X size={16} /></button>
            </div>
            <div className="mt-5"><VideoPlayer url={watching.video_url} /></div>
            {watching.description && <p className="font-soft text-sm mt-4" style={{ color: "var(--text-secondary)" }}>{watching.description}</p>}
            <a href={watching.video_url} target="_blank" rel="noreferrer" className="font-label text-[10px] tracking-[0.24em] uppercase mt-4 inline-flex items-center gap-2" style={{ color: "var(--accent)" }}>
              Open in new tab <ArrowRight size={11} />
            </a>
          </div>
        </div>
      )}

      {/* Phase roadmap */}
      <section className="space-y-10">
        <p className="overline">The full path · 24 weeks</p>
        {byPhase.map((phase, pi) => (
          <div key={phase.name} data-testid={`phase-${phase.name.toLowerCase()}`}>
            <div className="flex items-baseline gap-4 mb-5">
              <span className="font-soft text-3xl" style={{ color: PHASE_COLORS[phase.name] }}>0{pi + 1}</span>
              <h3 className="font-display text-2xl">{phase.name}</h3>
              <span className="font-label text-[10px] tracking-widest uppercase" style={{ color: "var(--text-tertiary)" }}>
                Weeks {phase.weeks[0]?.week}–{phase.weeks[phase.weeks.length - 1]?.week}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {phase.weeks.map((w) => {
                const locked = !w.unlocked && !w.is_preview;
                const Tag = locked ? "div" : Link;
                const props = locked ? {} : { to: `/app/program/week/${w.week}` };
                return (
                  <Tag key={w.week} {...props}
                    data-testid={`week-${w.week}`}
                    className={`block p-5 border transition-colors ${locked ? "" : "hover:border-accent"}`}
                    style={{
                      borderColor: w.is_current ? "var(--accent)" : "var(--border-strong)",
                      background: w.is_current ? "rgba(168,152,128,0.08)" : "var(--surface-1)",
                      opacity: locked ? 0.55 : 1,
                      cursor: locked ? "not-allowed" : "pointer",
                    }}>
                    <div className="flex items-center justify-between">
                      <span className="font-label text-[10px] tracking-[0.22em] uppercase" style={{ color: w.is_current ? "var(--accent)" : "var(--text-tertiary)" }}>
                        Week {w.week}
                      </span>
                      {locked && <Lock size={11} style={{ color: "var(--text-tertiary)" }} />}
                      {w.is_current && <span className="font-label text-[9px] tracking-[0.22em] uppercase" style={{ color: "var(--accent)" }}>Now</span>}
                      {w.is_preview && <span className="font-label text-[9px] tracking-[0.22em] uppercase" style={{ color: "var(--accent)" }}>Preview</span>}
                    </div>
                    <h4 className="font-display text-base mt-3 leading-tight">{w.title}</h4>
                  </Tag>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
